import type { User } from '@talos/kyoko';
import { MixpanelEventSource } from '@talos/kyoko';
import type { FeatureFlags } from '../../../../hooks/useFeatureFlag';
import { isUserAuthorized } from '../../../../hooks/useRoleAuth';
import {
  PORTFOLIO_FULL_NAV,
  PORTFOLIO_LITE_NAV,
  PORTFOLIO_NAV_PARENT,
  type NavigationItem,
} from '../../../../tokens/navigationPaths';
import type { LayoutOptionTabProps, LayoutOptionView } from '../portfolioDashboard.types';
import type { PortfolioViewType } from '../portfolioDashboardLayouts';
import { getOperationsLayout } from './dockviewWorkspaceConfigs/operations/operations.defaultLayout';
import { getPortfolioManagementLayout } from './dockviewWorkspaceConfigs/portfolioManagement/portfolioManagement.defaultLayout';

type FlexibleLayoutProps = Extract<LayoutOptionTabProps, { layoutType: 'flexible' }>;
type FixedLayoutProps = Extract<LayoutOptionTabProps, { layoutType: 'fixed' }>;

type FlexibleLayoutConfig = Pick<
  FlexibleLayoutProps,
  'layoutType' | 'getDefaultLayout' | 'availablePanels' | 'activeFilters' | 'mixpanelEventSource'
>;
type FixedLayoutConfig = Pick<
  FixedLayoutProps,
  'layoutType' | 'content' | 'activeFilters' | 'activeControls' | 'mixpanelEventSource'
>;
// Layout-specific configuration for each tab (keyed by route)
// This contains all the layout-specific properties that are added to the base navigation structure
const LAYOUT_CONFIG: Record<`/${string}`, FlexibleLayoutConfig | FixedLayoutConfig> = {
  '/portfolio-management/overview': {
    layoutType: 'fixed',
    content: 'PM-Overview',
    activeFilters: ['SubAccounts'],
    activeControls: {
      showRollupHierarchy: true,
      menuOptions: ['includeCash', 'showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementOverview,
  },
  '/portfolio-management/performance': {
    layoutType: 'fixed',
    content: 'PM-Performance',
    activeFilters: ['SubAccounts'],
    activeControls: {
      showRollupHierarchy: true,
      menuOptions: ['includeCash', 'showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementPerformance,
  },
  '/portfolio-management/margin': {
    layoutType: 'fixed',
    content: 'PM-Margin',
    activeFilters: ['SubAccounts'],
    activeControls: {
      menuOptions: ['showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementMargin,
  },
  '/portfolio-management/transactions': {
    layoutType: 'flexible',
    getDefaultLayout: () => getPortfolioManagementLayout(),
    availablePanels: 'pm-transactions',
    activeFilters: ['SubAccounts'],
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementTransactions,
  },
  '/portfolio-management/reports': {
    layoutType: 'fixed',
    content: 'PM-Reports',
    activeFilters: ['SubAccounts'],
    activeControls: {
      menuOptions: ['showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementReports,
  },
  '/risk-management/exposures': {
    layoutType: 'fixed',
    content: 'Risk-Exposures',
    activeFilters: ['SubAccounts'],
    activeControls: {
      showRollupHierarchy: true,
      menuOptions: ['includeCash', 'showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioRiskExposures,
  },
  '/operations/overview': {
    layoutType: 'fixed',
    content: 'Operations-Overview',
    activeFilters: ['MarketAccounts'],
    activeControls: {
      menuOptions: ['showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsOverview,
  },
  '/operations/transactions': {
    layoutType: 'flexible',
    getDefaultLayout: () => getOperationsLayout(),
    availablePanels: 'operations-transactions',
    activeFilters: ['MarketAccounts'],
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsTransactions,
  },
  '/operations/otc-settlement': {
    layoutType: 'fixed',
    content: 'Operations-Settlement',
    activeFilters: [],
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsSettlement,
  },
  '/operations/lp-settlement': {
    layoutType: 'fixed',
    content: 'LP-Settlement',
    activeFilters: [],
    mixpanelEventSource: MixpanelEventSource.PortfolioLpSettlement,
  },
  '/operations/reconciliation': {
    layoutType: 'fixed',
    content: 'Operations-Reconciliation',
    activeFilters: [],
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsReconciliation,
  },
  '/operations/reports': {
    layoutType: 'fixed',
    content: 'Operations-Reports',
    activeFilters: ['MarketAccounts'],
    activeControls: {
      menuOptions: ['showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsReports,
  },
};

/**
 * Builds layout options from base navigation structure and layout configuration
 */
function buildLayoutOptions(portfolioNav: NavigationItem): LayoutOptionView<PortfolioViewType>[] {
  const result: LayoutOptionView<PortfolioViewType>[] = [];

  // Iterate through sections (Portfolio Management, Risk Management, Operations)
  for (const section of portfolioNav.children || []) {
    if (!section.path) {
      continue;
    }
    const sectionValue = section.path.split('/').pop() as PortfolioViewType;

    const layoutSection: LayoutOptionView<PortfolioViewType> = {
      label: section.label || '',
      route: section.path.replace(/^\/portfolio\//, '/') as `/${string}`,
      value: sectionValue,
      tabs: [],
    };

    // Iterate through tabs within each section
    for (const tab of section.children || []) {
      if (!tab.path) {
        continue;
      }
      // Strip /portfolio prefix to get the lookup key (e.g., '/operations/overview' from '/portfolio/operations/overview')
      const lookupKey = tab.path.replace(/^\/portfolio\//, '/') as `/${string}`;
      const layoutConfig = LAYOUT_CONFIG[lookupKey];

      if (!layoutConfig) {
        console.warn(`No layout configuration found for route: ${lookupKey} (from ${tab.path})`);
        continue;
      }

      // Extract just the relative route (e.g., '/overview' from '/portfolio/operations/overview')
      const relativeRoute = tab.path.substring(tab.path.lastIndexOf('/')) as `/${string}`;

      const tabConfig: LayoutOptionView<PortfolioViewType>['tabs'][number] = {
        label: tab.label || '',
        route: relativeRoute,
        ...layoutConfig,
      } as LayoutOptionView<PortfolioViewType>['tabs'][number];

      // Convert NavigationItem visibility to LayoutOptionTab visibility
      if (tab.isVisible) {
        tabConfig.isVisibleIf = (flags: FeatureFlags, user: User) => {
          // Check visibility callback
          if (tab.isVisible && !tab.isVisible(flags, permission => isUserAuthorized(user, permission))) {
            return false;
          }

          return true;
        };
      }

      layoutSection.tabs.push(tabConfig);
    }

    // Only add sections that have tabs
    if (layoutSection.tabs.length > 0) {
      result.push(layoutSection);
    }
  }

  return result;
}

export function getPortfolioLayoutOptions(): LayoutOptionView<PortfolioViewType>[] {
  const portfolioNav = { ...PORTFOLIO_NAV_PARENT };
  portfolioNav.children = PORTFOLIO_FULL_NAV;
  return buildLayoutOptions(portfolioNav);
}

export function getPortfolioLiteLayoutOptions(): LayoutOptionView<PortfolioViewType>[] {
  const portfolioNav = { ...PORTFOLIO_NAV_PARENT };
  portfolioNav.children = PORTFOLIO_LITE_NAV;
  return buildLayoutOptions(portfolioNav);
}
